import { Badge } from '@artic/ui/badge';
import { Button } from '@artic/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@artic/ui/card';
import { useFeatureFlag } from '@artic/ui/feature-flags';
import { Skeleton } from '@artic/ui/skeleton';
import { toast } from '@artic/ui/toast';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQueryState } from 'nuqs';
import { useMemo } from 'react';

import { usePartnerId } from '../../features/inbox/use-partner-name';
import { projectBreadcrumbBadges } from '../../features/project-planning/project-route-chrome';
import { ProjectStepper } from '../../features/project-planning/project-stepper';
import {
  projectPlanningDetailQueryOptions,
  useRedraftProject,
  useReopenForEdit,
} from '../../features/project-planning/queries';
import { projectActionSearchParsers } from '../../features/project-planning/search';

export const Route = createFileRoute('/projects/$projectId_/redraft')({
  staticData: {
    breadcrumb: (match, queryClient) => {
      const { projectId } = match.params as { projectId: string };
      const data = queryClient.getQueryData(projectPlanningDetailQueryOptions(projectId).queryKey);
      return data ? `${data.project_name} — Redraft` : 'Project Redraft';
    },
    breadcrumbBadges: projectBreadcrumbBadges,
    layout: 'full-height',
  },
  // Non-blocking prefetch (not awaited): warms the project detail on hover via
  // defaultPreload: 'intent'; the component's useQuery surfaces load errors.
  // See routes/projects/$projectId.tsx.
  loader: ({ context: { queryClient }, params: { projectId } }) => {
    void queryClient.prefetchQuery(projectPlanningDetailQueryOptions(projectId));
  },
  component: ProjectRedraftPage,
});

function ProjectRedraftPage() {
  const planner = useFeatureFlag('planner');

  if (!planner) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-20">
        <h2 className="text-lg font-semibold">Feature Not Available</h2>
        <p className="text-muted-foreground text-sm">
          Production Planning is not enabled for your organisation.
        </p>
      </div>
    );
  }

  return <ProjectRedraftContent />;
}

function ProjectRedraftContent() {
  const { projectId } = Route.useParams();
  const partnerId = usePartnerId();
  const navigate = useNavigate();

  // `?ids=<uuid>,<uuid>` scopes the redraft to the planner's current
  // selection; absent means the whole project. The toolbar's
  // "Redraft N work orders" button populates this; the stepper's "Redraft"
  // link drops it. Defensively intersected with the project's actual rows
  // below so stale URLs don't claim phantom WOs.
  const [selectedIds] = useQueryState('ids', projectActionSearchParsers.ids);

  const {
    data: project,
    isLoading,
    isError,
  } = useQuery(projectPlanningDetailQueryOptions(projectId));

  const reopenForEdit = useReopenForEdit(projectId, partnerId ?? '');
  const redraftProject = useRedraftProject(projectId);

  // Counts split by current status. `scheduledIds` is what the Redraft button
  // sends to the backend; the other two are surfaced as context so the user
  // understands what will and won't be touched. When `selectedIds` is set
  // every count is scoped to that subset.
  const isSubsetScope = selectedIds.length > 0;
  const { scheduledIds, redraftingCount, draftCount, inProgressCount, completeCount } =
    useMemo(() => {
      const scheduled: string[] = [];
      let redrafting = 0;
      let draft = 0;
      let inProgress = 0;
      let complete = 0;
      const scopeSet = isSubsetScope ? new Set(selectedIds) : null;
      for (const wo of project?.work_orders ?? []) {
        if (scopeSet && !scopeSet.has(wo.work_order_id)) continue;
        switch (wo.work_order_current_status) {
          case 'scheduled':
            scheduled.push(wo.work_order_id);
            break;
          case 'redrafting':
            redrafting += 1;
            break;
          case 'draft':
            draft += 1;
            break;
          case 'in_progress':
            inProgress += 1;
            break;
          case 'complete':
            complete += 1;
            break;
          default:
            break;
        }
      }
      return {
        scheduledIds: scheduled,
        redraftingCount: redrafting,
        draftCount: draft,
        inProgressCount: inProgress,
        completeCount: complete,
      };
    }, [project?.work_orders, isSubsetScope, selectedIds]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 p-4">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-[40vh] w-full" />
      </div>
    );
  }

  if (isError || !project) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-20">
        <h2 className="text-lg font-semibold">Error Loading Project</h2>
        <p className="text-muted-foreground text-sm">
          Could not load the project. Please try again.
        </p>
      </div>
    );
  }

  // The stepper's Redrafting badge reflects project-wide state, not the
  // scoped subset — `project_status` is a stored tri-state (ENG-830), so this
  // reads it directly rather than deriving from work_orders.
  const isProjectRedrafting = project.project_status === 'redrafting';
  const isDraft = project.project_status === 'draft';
  const hasNothingToRedraft = scheduledIds.length === 0;
  // Subset scope keeps the existing per-row gate (the reopen-for-edit
  // endpoint needs partner scope). Whole-project scope has no such gate — the
  // new endpoint is valid even with zero eligible rows, which is the ENG-830
  // fix: a released project with nothing to reopen can still be redrafted so
  // new work orders can be added.
  const canRedraftSubset = hasNothingToRedraft ? false : !!partnerId;

  function handleRedraftSubset() {
    if (!canRedraftSubset) return;
    reopenForEdit.mutate(scheduledIds, {
      onSuccess: (result) => {
        const updated = result.updated.length;
        toast.success(
          updated === 0
            ? 'No work orders changed'
            : `Redrafting ${updated} work order${updated === 1 ? '' : 's'}`,
        );
        void navigate({ to: '/projects/$projectId', params: { projectId } });
      },
      onError: (err: Error) => {
        toast.error(err.message || 'Failed to redraft work orders');
      },
    });
  }

  // Whole-project scope: never pass work_order_ids — the server computes the
  // eligible set itself. A project can have more than 500 scheduled rows (the
  // array would be silently capped), and this path must succeed even with
  // zero of them, which is exactly what omitting the field is for.
  function handleRedraftProject() {
    redraftProject.mutate(undefined, {
      onSuccess: () => {
        toast.success(
          hasNothingToRedraft
            ? 'Project reopened for redrafting'
            : `Redrafting ${scheduledIds.length} work order${scheduledIds.length === 1 ? '' : 's'}`,
        );
        void navigate({ to: '/projects/$projectId', params: { projectId } });
      },
      onError: (err: Error) => {
        toast.error(err.message || 'Failed to redraft project');
      },
    });
  }

  const isSubmitting = isSubsetScope ? reopenForEdit.isPending : redraftProject.isPending;
  const handleSubmit = isSubsetScope ? handleRedraftSubset : handleRedraftProject;

  return (
    <div className="flex h-full flex-col">
      <ProjectStepper
        projectId={projectId}
        active="redraft"
        projectStatus={project.project_status}
        isProjectRedrafting={isProjectRedrafting}
      />

      <div className="flex flex-1 items-start justify-center overflow-auto p-8">
        <Card className="w-full max-w-2xl">
          {isDraft ? (
            <>
              <CardHeader>
                <CardTitle>Project is in Draft</CardTitle>
                <CardDescription>
                  There&apos;s nothing to redraft — all work orders are already editable.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 text-sm">
                  <Badge variant="default">draft</Badge>
                </div>
              </CardContent>
              <CardFooter>
                <Button variant="outline" asChild>
                  <Link to="/projects/$projectId" params={{ projectId }}>
                    Back to project
                  </Link>
                </Button>
              </CardFooter>
            </>
          ) : isSubsetScope && hasNothingToRedraft ? (
            <>
              <CardHeader>
                <CardTitle>Nothing to redraft</CardTitle>
                <CardDescription>
                  None of the selected rows are scheduled — only Scheduled rows can be redrafted. In
                  Process and Complete rows are locked; rows already in Redrafting need re-release
                  instead.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
                  <dt className="text-muted-foreground">Already redrafting</dt>
                  <dd>{redraftingCount}</dd>
                  <dt className="text-muted-foreground">In process (locked)</dt>
                  <dd>{inProgressCount}</dd>
                  <dt className="text-muted-foreground">Complete (locked)</dt>
                  <dd>{completeCount}</dd>
                </dl>
              </CardContent>
              <CardFooter>
                <Button variant="outline" asChild>
                  <Link to="/projects/$projectId" params={{ projectId }}>
                    Back to project
                  </Link>
                </Button>
              </CardFooter>
            </>
          ) : hasNothingToRedraft && isProjectRedrafting && redraftingCount === 0 ? (
            <>
              {/* ENG-830 made this state common: a project redrafted while it had
                  nothing to reopen, whose rows are now all new Drafts. Nothing is
                  `redrafting`, so the "already redrafting" copy below would be
                  actively wrong — these rows need releasing, not re-releasing. */}
              <CardHeader>
                <CardTitle>New work orders ready to release</CardTitle>
                <CardDescription>
                  This project is redrafting and has no Scheduled rows to reopen. Its{' '}
                  {draftCount === 1 ? 'work order is' : `${draftCount} work orders are`} still in
                  Draft — release {draftCount === 1 ? 'it' : 'them'} to put{' '}
                  {draftCount === 1 ? 'it' : 'them'} into production.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
                  <dt className="text-muted-foreground">Draft (ready to release)</dt>
                  <dd>{draftCount}</dd>
                  <dt className="text-muted-foreground">In process (locked)</dt>
                  <dd>{inProgressCount}</dd>
                  <dt className="text-muted-foreground">Complete (locked)</dt>
                  <dd>{completeCount}</dd>
                </dl>
              </CardContent>
              <CardFooter className="flex justify-end gap-2">
                <Button variant="outline" asChild>
                  <Link to="/projects/$projectId" params={{ projectId }}>
                    Back to project
                  </Link>
                </Button>
                <Button asChild>
                  <Link to="/projects/$projectId/release" params={{ projectId }}>
                    Release Work Orders
                  </Link>
                </Button>
              </CardFooter>
            </>
          ) : hasNothingToRedraft && isProjectRedrafting ? (
            <>
              <CardHeader>
                <CardTitle>Nothing to redraft</CardTitle>
                <CardDescription>
                  All eligible work orders are already redrafting. Re-release them from the work
                  order grid when you&apos;re done.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
                  <dt className="text-muted-foreground">Already redrafting</dt>
                  <dd>{redraftingCount}</dd>
                  <dt className="text-muted-foreground">Draft (ready to release)</dt>
                  <dd>{draftCount}</dd>
                  <dt className="text-muted-foreground">In process (locked)</dt>
                  <dd>{inProgressCount}</dd>
                  <dt className="text-muted-foreground">Complete (locked)</dt>
                  <dd>{completeCount}</dd>
                </dl>
              </CardContent>
              <CardFooter>
                <Button variant="outline" asChild>
                  <Link to="/projects/$projectId" params={{ projectId }}>
                    Back to project
                  </Link>
                </Button>
              </CardFooter>
            </>
          ) : hasNothingToRedraft ? (
            <>
              {/* ENG-830: a released project with nothing eligible to reopen —
                  empty, or every row Archived / In Process / Complete — can
                  still be redrafted. There is nothing to *reopen*, but
                  redrafting still opens the project so new work orders can be
                  added, manually or via import. */}
              <CardHeader>
                <CardTitle>Redraft this project?</CardTitle>
                <CardDescription>
                  There are no Scheduled work orders to reopen — every work order is Archived, In
                  Process, or Complete (or there are none at all). Redrafting still opens the
                  project for new work: once redrafted, you can add work orders manually or via
                  import.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
                  <dt className="text-muted-foreground">Project</dt>
                  <dd className="font-medium">{project.project_name}</dd>
                  <dt className="text-muted-foreground">In process (locked)</dt>
                  <dd>{inProgressCount}</dd>
                  <dt className="text-muted-foreground">Complete (locked)</dt>
                  <dd>{completeCount}</dd>
                </dl>
              </CardContent>
              <CardFooter className="flex justify-end gap-2">
                <Button variant="outline" asChild>
                  <Link to="/projects/$projectId" params={{ projectId }}>
                    Cancel
                  </Link>
                </Button>
                <Button onClick={handleRedraftProject} disabled={redraftProject.isPending}>
                  {redraftProject.isPending ? 'Redrafting…' : 'Redraft Project'}
                </Button>
              </CardFooter>
            </>
          ) : (
            <>
              <CardHeader>
                <CardTitle>
                  {isSubsetScope
                    ? `Redraft ${scheduledIds.length} work order${scheduledIds.length === 1 ? '' : 's'}?`
                    : 'Redraft this project?'}
                </CardTitle>
                <CardDescription>
                  Scheduled work orders will be reopened for edit. DFMA Containers (except Product
                  Type) and the configured Standard Columns become editable again. Work Order Name
                  and Product Type stay locked. In-progress and complete work orders are unaffected.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
                  <dt className="text-muted-foreground">Project</dt>
                  <dd className="font-medium">{project.project_name}</dd>
                  <dt className="text-muted-foreground">Will redraft</dt>
                  <dd>
                    {scheduledIds.length} scheduled work order
                    {scheduledIds.length === 1 ? '' : 's'}
                    {isSubsetScope ? ' from your selection' : ''}
                  </dd>
                  {redraftingCount > 0 && (
                    <>
                      <dt className="text-muted-foreground">Already redrafting</dt>
                      <dd>{redraftingCount}</dd>
                    </>
                  )}
                  {inProgressCount > 0 && (
                    <>
                      <dt className="text-muted-foreground">In process (skipped)</dt>
                      <dd>{inProgressCount}</dd>
                    </>
                  )}
                  {completeCount > 0 && (
                    <>
                      <dt className="text-muted-foreground">Complete (skipped)</dt>
                      <dd>{completeCount}</dd>
                    </>
                  )}
                </dl>
              </CardContent>
              <CardFooter className="flex justify-end gap-2">
                <Button variant="outline" asChild>
                  <Link to="/projects/$projectId" params={{ projectId }}>
                    Cancel
                  </Link>
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={isSubsetScope ? !canRedraftSubset || isSubmitting : isSubmitting}
                >
                  {isSubmitting
                    ? isSubsetScope && reopenForEdit.progress
                      ? `Redrafting ${reopenForEdit.progress.done}/${reopenForEdit.progress.total}…`
                      : 'Redrafting…'
                    : isSubsetScope
                      ? `Redraft ${scheduledIds.length} work order${scheduledIds.length === 1 ? '' : 's'}`
                      : 'Redraft Project'}
                </Button>
              </CardFooter>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
